import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, ListPlus, Repeat } from "lucide-react";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  deleteCashflowItem,
  deleteCashflowSeries,
  listCashflowCategories,
  listCashflowItems,
  listCashflowSeries,
} from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRub, formatRuDate } from "@/lib/format";
import { ConfirmDeleteButton, ErrorPanel } from "@/pages/cashflow/ManualFields";
import { ManualBulkForm } from "@/pages/cashflow/ManualBulkForm";
import { ManualItemForm } from "@/pages/cashflow/ManualItemForm";
import { ManualSeriesForm } from "@/pages/cashflow/ManualSeriesForm";
import { SalaryImportCard } from "@/pages/cashflow/SalaryImportCard";
import {
  FIRMNESS_OPTIONS,
  WEEKDAY_OPTIONS,
  errorDetail,
  orgLabel,
} from "@/pages/cashflow/utils";
import type {
  CashflowManualSeriesView,
  CashflowOrg,
} from "@/types/api";

type OpenForm = "none" | "series" | "item" | "bulk";

function firmnessLabel(value: string): string {
  return FIRMNESS_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function ruleLabel(s: CashflowManualSeriesView): string {
  if (s.rule_kind === "monthly") return `ежемесячно, ${s.day_of_month}-го`;
  const day = WEEKDAY_OPTIONS.find((o) => o.value === s.weekday)?.label ?? String(s.weekday);
  return `еженедельно, ${day.toLowerCase()}`;
}

interface Props {
  orgInns: string[];
  orgs: CashflowOrg[];
  /**
   * Дата расчёта календаря (с бэка): от неё показываем разовые строки и от
   * неё же формы ограничивают дату платежа — бэк сравнивает с московским
   * «сегодня», поэтому дата браузера тут не годится.
   */
  asOf: string;
  /** Вызывается после любого изменения — страница пересчитывает календарь. */
  onChanged: () => void;
}

export function ManualPanel({ orgInns, orgs, asOf, onChanged }: Props): React.ReactElement {
  const qc = useQueryClient();
  const [openForm, setOpenForm] = useState<OpenForm>("none");
  // Строки показываем ровно с той даты, на которую считается календарь, иначе
  // в списке не те строки, что попали в расчёт.
  const from = asOf;

  const categoriesQ = useQuery({
    queryKey: ["cashflow-categories"],
    queryFn: listCashflowCategories,
    staleTime: 5 * 60_000,
  });
  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data]);
  const categoryLabel = (key: string): string =>
    categories.find((c) => c.key === key)?.label ?? key;

  const seriesQ = useQuery({ queryKey: ["cashflow-series"], queryFn: listCashflowSeries });
  const itemsQ = useQuery({
    queryKey: ["cashflow-items", from],
    queryFn: () => listCashflowItems({ dateFrom: from }),
  });

  const refresh = (queryKey: string) => {
    qc.invalidateQueries({ queryKey: [queryKey] });
    onChanged();
  };

  const deleteSeriesMut = useMutation({
    mutationFn: deleteCashflowSeries,
    onSuccess: () => {
      toast.success("Серия удалена (её строки остались)");
      refresh("cashflow-series");
    },
    onError: (err: unknown) =>
      toast.error("Не удалось удалить серию", { description: errorDetail(err) }),
  });

  const deleteItemMut = useMutation({
    mutationFn: deleteCashflowItem,
    onSuccess: () => {
      toast.success("Строка удалена");
      refresh("cashflow-items");
    },
    onError: (err: unknown) =>
      toast.error("Не удалось удалить строку", { description: errorDetail(err) }),
  });

  // Без справочника статей форму заполнить нечем — не показываем пустой select,
  // а прямо говорим, почему нельзя добавлять.
  const categoriesError = categoriesQ.isError ? errorDetail(categoriesQ.error) : null;
  const series = seriesQ.data ?? [];
  const items = itemsQ.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Ручные строки</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={openForm === "series" ? "default" : "outline"}
            disabled={categoriesError !== null}
            onClick={() => setOpenForm(openForm === "series" ? "none" : "series")}
          >
            <Repeat className="mr-2 h-4 w-4" />
            Добавить серию
          </Button>
          <Button
            size="sm"
            variant={openForm === "item" ? "default" : "outline"}
            disabled={categoriesError !== null}
            onClick={() => setOpenForm(openForm === "item" ? "none" : "item")}
          >
            <CalendarPlus className="mr-2 h-4 w-4" />
            Добавить строку
          </Button>
          <Button
            size="sm"
            variant={openForm === "bulk" ? "default" : "outline"}
            disabled={categoriesError !== null}
            onClick={() => setOpenForm(openForm === "bulk" ? "none" : "bulk")}
          >
            <ListPlus className="mr-2 h-4 w-4" />
            Вставить пачкой
          </Button>
        </div>

        {categoriesError !== null && (
          <ErrorPanel detail={`Справочник статей не загрузился: ${categoriesError}`}>
            <p className="text-xs">
              Пока статьи не загрузятся, добавлять серии и строки нельзя — статья обязательна.
            </p>
          </ErrorPanel>
        )}

        {categoriesError === null && openForm === "series" && (
          <ManualSeriesForm
            orgInns={orgInns}
            orgs={orgs}
            categories={categories}
            onCreated={() => {
              setOpenForm("none");
              refresh("cashflow-series");
            }}
            onCancel={() => setOpenForm("none")}
          />
        )}
        {categoriesError === null && openForm === "item" && (
          <ManualItemForm
            orgInns={orgInns}
            orgs={orgs}
            categories={categories}
            asOf={asOf}
            onCreated={() => {
              setOpenForm("none");
              refresh("cashflow-items");
            }}
            onCancel={() => setOpenForm("none")}
          />
        )}
        {categoriesError === null && openForm === "bulk" && (
          <ManualBulkForm
            orgInns={orgInns}
            orgs={orgs}
            categories={categories}
            asOf={asOf}
            onCreated={() => {
              setOpenForm("none");
              refresh("cashflow-items");
            }}
            onCancel={() => setOpenForm("none")}
          />
        )}

        <SalaryImportCard asOf={asOf} onImported={onChanged} />

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">
            Серии {seriesQ.isError ? "" : `(${series.length})`}
          </h3>
          {seriesQ.isError ? (
            <ErrorPanel detail={`Серии не загрузились: ${errorDetail(seriesQ.error)}`} />
          ) : series.length === 0 ? (
            <p className="text-sm text-muted-foreground">Серий пока нет</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Организация</TableHead>
                  <TableHead>Статья</TableHead>
                  <TableHead>Правило</TableHead>
                  <TableHead className="text-right">Сумма</TableHead>
                  <TableHead>Период</TableHead>
                  <TableHead>Твёрдость</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {series.map((s) => (
                  <TableRow key={s.id} className={s.is_active ? "" : "text-muted-foreground"}>
                    <TableCell>
                      {s.title}
                      {!s.is_active && " (выключена)"}
                    </TableCell>
                    <TableCell>{orgLabel(s.org_inn, orgs)}</TableCell>
                    <TableCell>{categoryLabel(s.category)}</TableCell>
                    <TableCell>{ruleLabel(s)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRub(s.amount)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatRuDate(s.start_date)} — {s.end_date ? formatRuDate(s.end_date) : "∞"}
                    </TableCell>
                    <TableCell>{firmnessLabel(s.firmness)}</TableCell>
                    <TableCell className="text-right">
                      <ConfirmDeleteButton
                        disabled={deleteSeriesMut.isPending}
                        onConfirm={() => deleteSeriesMut.mutate(s.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">
            Разовые строки с {formatRuDate(from)} {itemsQ.isError ? "" : `(${items.length})`}
          </h3>
          {itemsQ.isError ? (
            <ErrorPanel detail={`Разовые строки не загрузились: ${errorDetail(itemsQ.error)}`} />
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Строк пока нет</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Название</TableHead>
                  <TableHead>Организация</TableHead>
                  <TableHead>Статья</TableHead>
                  <TableHead className="text-right">Сумма</TableHead>
                  <TableHead>Твёрдость</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="whitespace-nowrap">{formatRuDate(it.date)}</TableCell>
                    <TableCell>{it.title}</TableCell>
                    <TableCell>{orgLabel(it.org_inn, orgs)}</TableCell>
                    <TableCell>{categoryLabel(it.category)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRub(it.amount)}
                    </TableCell>
                    <TableCell>{firmnessLabel(it.firmness)}</TableCell>
                    <TableCell className="text-right">
                      <ConfirmDeleteButton
                        disabled={deleteItemMut.isPending}
                        onConfirm={() => deleteItemMut.mutate(it.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
